import { useState, useEffect, useCallback, useRef } from 'react';
import { VideoRecorder } from '../VideoRecorder';
import { 
  RecorderConfig, 
  RecordingResult, 
  ProgressEvent, 
  ProcessingResult, 
  ErrorEvent 
} from '../types';

interface UseVideoRecorderOptions {
  config: RecorderConfig;
  onComplete?: (result: ProcessingResult) => void;
  onError?: (error: ErrorEvent) => void;
  onProgress?: (progress: ProgressEvent) => void;
}

interface UseVideoRecorderReturn {
  // State
  isInitialized: boolean;
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  progress: number;
  status: string;
  error: string | null;
  lastRecording: RecordingResult | null;
  
  // Actions
  initialize: () => Promise<void>;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<RecordingResult>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  uploadRecording: (blob: Blob, metadata?: object) => Promise<any>;
  clearError: () => void;
  
  // Utilities
  getRecordingState: () => any;
  cleanup: () => void;
}

export const useVideoRecorder = ({
  config,
  onComplete,
  onError,
  onProgress
}: UseVideoRecorderOptions): UseVideoRecorderReturn => {
  const [recorder, setRecorder] = useState<VideoRecorder | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Ready to initialize');
  const [error, setError] = useState<string | null>(null);
  const [lastRecording, setLastRecording] = useState<RecordingResult | null>(null);
  
  const durationIntervalRef = useRef<NodeJS.Timeout>();

  // Initialize recorder when config changes
  useEffect(() => {
    const newRecorder = new VideoRecorder(config);
    
    // Set up event listeners
    newRecorder.onProgress((progressEvent: ProgressEvent) => {
      setProgress(progressEvent.progress);
      onProgress?.(progressEvent);
      
      if (progressEvent.type === 'upload') {
        setStatus(`Uploading: ${Math.round(progressEvent.progress)}%`);
      } else {
        setStatus(`Processing: ${progressEvent.stage || 'In progress'}`);
      }
    });

    newRecorder.onComplete((result: ProcessingResult) => {
      setProgress(0);
      setStatus('Processing completed!');
      onComplete?.(result);
    });

    newRecorder.onError((errorEvent: ErrorEvent) => {
      setError(errorEvent.message);
      setStatus('Error occurred');
      onError?.(errorEvent);
    });

    setRecorder(newRecorder);

    return () => {
      newRecorder.cleanup();
    };
  }, [config, onComplete, onError, onProgress]);

  // Update duration during recording
  useEffect(() => {
    if (isRecording && !isPaused && recorder) {
      durationIntervalRef.current = setInterval(() => {
        const state = recorder.getRecordingState();
        setDuration(Math.round(state.duration));
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [isRecording, isPaused, recorder]);

  const initialize = useCallback(async () => {
    if (!recorder) return;

    try {
      setError(null);
      setStatus('Initializing camera...');
      
      await recorder.initialize();
      
      setIsInitialized(true);
      setStatus('Camera ready');
    } catch (err) {
      const errorMessage = `Failed to access camera: ${(err as Error).message}`;
      setError(errorMessage);
      setStatus('Initialization failed');
      throw err;
    }
  }, [recorder]);

  const startRecording = useCallback(async () => {
    if (!recorder) return;

    try {
      setError(null);
      await recorder.startRecording();
      
      setIsRecording(true);
      setIsPaused(false);
      setDuration(0);
      setStatus('Recording...');
    } catch (err) {
      const errorMessage = `Failed to start recording: ${(err as Error).message}`;
      setError(errorMessage);
      throw err;
    }
  }, [recorder]);

  const stopRecording = useCallback(async () => {
    if (!recorder) throw new Error('Recorder not initialized');

    try {
      setStatus('Stopping recording...');
      
      const recording = await recorder.stopRecording();
      setLastRecording(recording);
      setIsRecording(false);
      setIsPaused(false);
      setStatus('Recording stopped');
      
      return recording;
    } catch (err) {
      const errorMessage = `Failed to stop recording: ${(err as Error).message}`;
      setError(errorMessage);
      throw err;
    }
  }, [recorder]);

  const pauseRecording = useCallback(() => {
    if (!recorder) return;

    recorder.pauseRecording();
    setIsPaused(true);
    setStatus('Recording paused');
  }, [recorder]);

  const resumeRecording = useCallback(() => {
    if (!recorder) return;

    recorder.resumeRecording();
    setIsPaused(false);
    setStatus('Recording resumed');
  }, [recorder]);

  const uploadRecording = useCallback(async (blob: Blob, metadata?: object) => {
    if (!recorder) throw new Error('Recorder not initialized');

    try {
      setStatus('Uploading...');
      setProgress(0);
      
      const result = await recorder.uploadRecording(blob, metadata as Record<string, unknown>);
      setStatus('Upload completed');
      
      return result;
    } catch (err) {
      const errorMessage = `Upload failed: ${(err as Error).message}`;
      setError(errorMessage);
      throw err;
    }
  }, [recorder]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getRecordingState = useCallback(() => {
    return recorder?.getRecordingState() || {
      isRecording: false,
      isPaused: false,
      duration: 0
    };
  }, [recorder]);

  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    recorder?.cleanup();
  }, [recorder]);

  return {
    // State
    isInitialized,
    isRecording,
    isPaused,
    duration,
    progress,
    status,
    error,
    lastRecording,
    
    // Actions
    initialize,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    uploadRecording,
    clearError,
    
    // Utilities
    getRecordingState,
    cleanup
  };
};